import { UIEvent, useMemo, useState } from "react";
import { AppId, capitalize, cssX, FCX, ProductLogo } from "@datassential/platform-ui-lib";
import { useParams } from "react-router-dom";
import cn from "classnames";
import useCategoryProfile from "@apiHooks/useCategoryProfile";
import useCategoryProfileDetails from "@apiHooks/useCategoryProfileDetails";
import { getSectionId } from "@helpers";
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import TabList from "@components/new/TabList/TabList";
import CategoryProfileSidebar from "./components/CategoryProfileSidebar/CategoryProfileSidebar";
import CategoryProfileDivider from "./components/CategoryProfileDivider/CategoryProfileDivider";
import CategoryProfileSwitcher from "./components/CategoryProfileSwitcher/CategoryProfileSwitcher";
import CategoryProfileHighlights, { useHighlightsSectionData } from "./sections/Highlights/Highlights";
import { useMenuSectionData } from "./sections/Menu/Menu";
import { useTrendingNowSectionData } from "./sections/TrendingNow/TrendingNow";
import { useLaunchesSectionData } from "./sections/Launches/Launches";
import { useConsumerSectionData } from "./sections/Consumer/Consumer";
import { useBrandPerformanceSectionData } from "./sections/BrandPerformance/BrandPerformance";
import { useReportsSectionData } from "./sections/Reports/Reports";
import {
    CATEGORY_PROFILE_CSS_VARS,
    CATEGORY_PROFILE_HEADER_HEIGHT,
    CATEGORY_PROFILE_SCROLLBAR_ID,
    CATEGORY_PROFILE_SECTIONS,
    CATEGORY_PROFILE_TAB_OFFSET,
    CategoryProfileSectionDataState,
    CategoryProfileSectionId,
    DEFAULT_CATEGORY_PROFILE_SECTION_STATE,
} from "./CategoryProfile.constants";
import "./CategoryProfile.styles.scss";

const CategoryProfile: FCX = () => {
    const { id: categoryId } = useParams<{ id: string }>();
    const { data, isLoading } = useCategoryProfile(categoryId);
    const { data: details, isLoading: isDetailsLoading } = useCategoryProfileDetails(categoryId);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const categoryName = useMemo(() => capitalize(data?.name ?? ""), [data]);

    const highlightsSectionState = useHighlightsSectionData(categoryId);
    const menuSectionState = useMenuSectionData(categoryId);
    const trendingNowSectionState = useTrendingNowSectionData(categoryId, data?.name ?? "");
    const launchesSectionState = useLaunchesSectionData(categoryId, details?.lrCategories);
    const consumerSectionState = useConsumerSectionData(categoryId);
    const brandPerformanceSectionState = useBrandPerformanceSectionData(categoryId);
    const reportsSectionState = useReportsSectionData(categoryId);

    const stateBySectionId: Partial<Record<CategoryProfileSectionId, CategoryProfileSectionDataState>> = useMemo(
        () => ({
            [CategoryProfileSectionId.Menu]: menuSectionState,
            [CategoryProfileSectionId.TrendingNow]: trendingNowSectionState,
            [CategoryProfileSectionId.Launches]: launchesSectionState,
            [CategoryProfileSectionId.Consumer]: consumerSectionState,
            [CategoryProfileSectionId.BrandPerformance]: brandPerformanceSectionState,
            [CategoryProfileSectionId.Reports]: reportsSectionState,
        }),
        [menuSectionState, trendingNowSectionState, launchesSectionState, consumerSectionState, brandPerformanceSectionState, reportsSectionState],
    );

    const visibleSections = useMemo(
        () => CATEGORY_PROFILE_SECTIONS
            .map(section => ({
                section,
                state: stateBySectionId[section.id] ?? DEFAULT_CATEGORY_PROFILE_SECTION_STATE,
            }))
            .filter(({ state }) => state.hasData),
        [stateBySectionId],
    );

    return (
        /* @ts-ignore */
        <Page>
            <PageContent>
                <CustomBaseScrollbars
                    id={CATEGORY_PROFILE_SCROLLBAR_ID}
                    onScroll={(event: UIEvent<HTMLElement>) => {
                        setIsScrolled(event.currentTarget.scrollTop >= CATEGORY_PROFILE_HEADER_HEIGHT);
                    }}
                >
                    <div className="CategoryProfile" style={CATEGORY_PROFILE_CSS_VARS}>
                        <div className="CategoryProfile__header">
                            <span className="CategoryProfile__header-title">
                                Category Profile
                            </span>
                        </div>

                        {(isLoading || isDetailsLoading || !data) ? (
                            <div className="spinner spinner-blink">
                                <ProductLogo appId={AppId.Snap} type="regular"/>
                            </div>
                        ) : (
                            <>
                                <div
                                    className={cn(
                                        "CategoryProfile__nav",
                                        cssX("-use-rem"),
                                        isScrolled && "CategoryProfile__nav--scrolled",
                                    )}
                                >
                                    <h1
                                        className="CategoryProfile__nav-title"
                                        data-testid="textCategoryProfileCategoryName"
                                    >
                                        {data.name ? (
                                            <CategoryProfileSwitcher currentId={categoryId} currentName={data.name} />
                                        ) : (
                                            data.name
                                        )}
                                    </h1>
                                    <TabList
                                        className="CategoryProfile__nav-tabs"
                                        data={visibleSections.map(({ section }) => section)}
                                        containerId={CATEGORY_PROFILE_SCROLLBAR_ID}
                                        offset={CATEGORY_PROFILE_TAB_OFFSET}
                                    />
                                </div>

                                <div className="CategoryProfile__wrapper">
                                    <div className="CategoryProfile__body">
                                        <CategoryProfileSidebar
                                            data={details}
                                            categoryName={categoryName}
                                            isCollapsed={isSidebarCollapsed}
                                            isScrolled={isScrolled}
                                        />

                                        <CategoryProfileDivider
                                            isCollapsed={isSidebarCollapsed}
                                            onToggle={() => setIsSidebarCollapsed(prev => !prev)}
                                        />

                                        <div className="CategoryProfile__main">
                                            {highlightsSectionState.hasData && (
                                                <div className="CategoryProfile__section">
                                                    <CategoryProfileHighlights
                                                        bullets={highlightsSectionState.data}
                                                        isLoading={highlightsSectionState.isLoading}
                                                    />
                                                </div>
                                            )}

                                            {visibleSections.map(({ section: { id, title, Component }, state }) => (
                                                <section
                                                    key={id}
                                                    id={getSectionId(id)}
                                                    className="CategoryProfile__section"
                                                    data-testid={`blockCategoryProfileSection-${id}`}
                                                >
                                                    <Component
                                                        title={`${title}: ${categoryName}`}
                                                        categoryName={categoryName}
                                                        data={state.data}
                                                    />
                                                </section>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </CustomBaseScrollbars>
            </PageContent>
        </Page>
    );
};

export default CategoryProfile;
